import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, LogOut, Check, Trash2, Briefcase, Save, Camera, Image as ImageIcon, Loader2, Pencil } from "lucide-react";
import { api, humanDate, money, today, normalizePhoneTR, normalizePlateTR, canEditWorkOrder, rowLink } from "../lib/api";
import { MoneyInput } from "../lib/ui";
import { RuhsatArchiveTable } from "./RuhsatArchive";
import Appointments from "./Appointments";
import { StockCombobox, ServiceCombobox } from "./Modals";
import { InstallsTabs } from "./Installs";

export const compressRuhsatImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    try {
      const maxW = 1200;
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.78));
    } catch (e) { reject(e); }
  };
  img.onerror = reject;
  img.src = url;
});

export function RuhsatCapture({ onParsed, onPhoto, hasPhoto }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!/^image\//.test(file.type || "")) {
      setError("Sadece görsel dosyalar desteklenir.");
      return;
    }
    try {
      setBusy(true);
      const dataUri = await compressRuhsatImage(file);
      onPhoto?.(dataUri);
      const b64 = dataUri.split(",", 2)[1];
      const { data } = await api.post("/ocr/ruhsat", { image_base64: b64, mime_type: "image/jpeg" });
      onParsed?.(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Ruhsat okunamadı. Fotoğraf kaydedildi, alanları elle doldurabilirsiniz.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ruhsat-capture" data-testid="ruhsat-capture">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
             data-testid="ruhsat-file-input"
             style={{ display: "none" }}
             onChange={(e) => handleFile(e.target.files?.[0])} />
      <input ref={galleryRef} type="file" accept="image/*"
             data-testid="ruhsat-gallery-input"
             style={{ display: "none" }}
             onChange={(e) => handleFile(e.target.files?.[0])} />
      <div className="ruhsat-capture-buttons">
        <button type="button" className="secondary-button" disabled={busy}
                data-testid="ruhsat-capture-btn"
                onClick={() => cameraRef.current?.click()}>
          {busy ? <><Loader2 size={14} className="spin" /> Okunuyor…</>
                : <><Camera size={14} /> {hasPhoto ? "Fotoğrafı değiştir" : "Fotoğraf çek"}</>}
        </button>
        <button type="button" className="secondary-button" disabled={busy}
                data-testid="ruhsat-gallery-btn"
                onClick={() => galleryRef.current?.click()}>
          <ImageIcon size={14} /> Galeriden seç
        </button>
      </div>
      <small className="muted">Fotoğraftan plaka, şase, sahibi, marka/model/yıl bilgileri otomatik doldurulur. Fotoğraf fiş kaydıyla birlikte saklanır; alanlar yanlış doldurulursa elle düzeltebilirsiniz.</small>
      {error && <div className="form-error" data-testid="ruhsat-error">{error}</div>}
    </div>
  );
}

export function WorkOrderForm({ onSaved, stocks = [], initial = null, editId = null, onCancel }) {
  const [form, setForm] = useState(initial || {
    customer_name: "", plate: "", chassis_no: "", job: "",
    items: [{ is_product: false, stock_id: null, code: "", name: "", qty: 1, unit_price: 0, description: "" }],
    note: "", vehicle_make: "", vehicle_model: "", vehicle_year: "",
    ruhsat_photo: null,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  const addLine = (is_product = false) => setForm({ ...form, items: [...form.items, { is_product, stock_id: null, code: "", name: "", qty: 1, unit_price: 0, description: "" }] });
  const removeLine = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });
  const updateLine = (i, patch) => setForm({ ...form, items: form.items.map((r, j) => j === i ? { ...r, ...patch } : r) });
  const pickStock = (i, s) => {
    if (!s) return;
    updateLine(i, { stock_id: s.id, code: s.code, name: s.name,
                    unit_price: s.price ?? 0, description: form.items[i].description || "" });
  };
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);
  const pickService = (i, s) => {
    if (!s) return;
    updateLine(i, { service_id: s.id, code: s.code, name: s.name,
                    unit_price: s.price || form.items[i].unit_price || 0, description: form.items[i].description || "" });
  };

  const total = useMemo(() => form.items.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0), [form.items]);

  const handleOcr = (data) => {
    setForm((f) => ({
      ...f,
      customer_name: data.customer_name || f.customer_name,
      plate: data.plate || f.plate,
      chassis_no: data.chassis_no || f.chassis_no,
      vehicle_make: data.make || f.vehicle_make,
      vehicle_model: data.model || f.vehicle_model,
      vehicle_year: data.year || f.vehicle_year,
      owner_id_no: data.owner_id_no || f.owner_id_no || "",
      owner_address: data.owner_address || f.owner_address || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name) return setError("Müşteri bilgisini girin.");
    try {
      setSaving(true);
      const body = {
        ...form,
        items: form.items.filter((r) => String(r.name || "").trim()).map((r) => ({
          ...r, qty: Number(r.qty) || 0, unit_price: Number(r.unit_price) || 0,
          is_product: !!r.is_product, stock_id: r.stock_id || null,
        })),
      };
      if (editId) {
        await api.put(`/work-orders/${editId}`, body);
        onSaved?.();
        return;
      }
      await api.post("/work-orders", body);
      setForm({ customer_name: "", plate: "", chassis_no: "", job: "",
                items: [{ is_product: false, stock_id: null, code: "", name: "", qty: 1, unit_price: 0, description: "" }],
                note: "", vehicle_make: "", vehicle_model: "", vehicle_year: "",
                ruhsat_photo: null });
      setSavedMsg("İş fişi muhasebeye gönderildi.");
      setTimeout(() => setSavedMsg(""), 4000);
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="work-order-form" data-testid="work-order-form">
      <div className="tab-row" data-testid="wo-vehicle-mode" style={{ marginBottom: 12 }}>
        <button type="button" data-testid="wo-mode-vehicle" className={!form.no_vehicle ? "tab active" : "tab"}
                onClick={() => setForm({ ...form, no_vehicle: false })}>Araçlı iş</button>
        <button type="button" data-testid="wo-mode-novehicle" className={form.no_vehicle ? "tab active" : "tab"}
                onClick={() => setForm({ ...form, no_vehicle: true, plate: "", chassis_no: "", vehicle_make: "", vehicle_model: "", vehicle_year: "", ruhsat_photo: null })}>Araçsız kalem</button>
      </div>
      {!form.no_vehicle && <RuhsatCapture
        onParsed={handleOcr}
        onPhoto={(dataUri) => setForm((f) => ({ ...f, ruhsat_photo: dataUri }))}
        hasPhoto={!!form.ruhsat_photo}
      />}
      {!form.no_vehicle && form.ruhsat_photo && (
        <div className="ruhsat-preview" data-testid="ruhsat-preview">
          <img src={form.ruhsat_photo} alt="Ruhsat önizleme" />
          <button type="button" className="icon-button" title="Fotoğrafı kaldır"
                  onClick={() => setForm((f) => ({ ...f, ruhsat_photo: null }))}>×</button>
        </div>
      )}
      {form.no_vehicle && <p className="muted" data-testid="wo-novehicle-hint">Araç olmadan yapılan iş / satış. Müşteri bilgilerini elle girin.</p>}
      <div className="form-grid two">
        <label>Müşteri (İsim / Firma)
          <input data-testid="wo-customer" value={form.customer_name}
                 onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required />
        </label>
        {!form.no_vehicle && <>
        <label>Plaka
          <input data-testid="wo-plate" value={form.plate}
                 onChange={(e) => setForm({ ...form, plate: e.target.value })}
                 onBlur={(e) => setForm({ ...form, plate: normalizePlateTR(e.target.value) })}
                 placeholder="34 ABC 123" />
        </label>
        <label>Şase No
          <input data-testid="wo-chassis" value={form.chassis_no}
                 onChange={(e) => setForm({ ...form, chassis_no: e.target.value })} />
        </label>
        <label>Marka
          <input data-testid="wo-make" value={form.vehicle_make}
                 onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} />
        </label>
        <label>Model / Yıl
          <input data-testid="wo-model" value={`${form.vehicle_model || ""}${form.vehicle_year ? " · " + form.vehicle_year : ""}`}
                 onChange={(e) => {
                   const [m, y] = e.target.value.split("·").map((v) => v.trim());
                   setForm({ ...form, vehicle_model: m || "", vehicle_year: y || form.vehicle_year });
                 }} />
        </label>
        </>}
        <label>{form.no_vehicle ? "TC Kimlik / Vergi No" : "TC Kimlik / Vergi No (ruhsattan)"}
          <input data-testid="wo-owner-id" value={form.owner_id_no || ""} inputMode="numeric" maxLength={11}
                 onChange={(e) => setForm({ ...form, owner_id_no: e.target.value.replace(/\D/g, "") })} />
        </label>
        <label>{form.no_vehicle ? "Adres" : "Adres (ruhsattan)"}
          <input data-testid="wo-owner-address" value={form.owner_address || ""}
                 onChange={(e) => setForm({ ...form, owner_address: e.target.value })} />
        </label>
      </div>

      <h3 className="form-section">Hizmet ve Ürünler</h3>
      <div className="wo-items">
        {form.items.map((row, i) => (
          <div key={i} className={`wo-item-row ${row.is_product ? "is-product" : ""}`} data-testid={`wo-item-${i}`}>
            <span className="wo-item-kind" data-testid={`wo-item-${i}-kind`}>
              {row.is_product ? "Ürün" : "Hizmet"}
            </span>
            {row.is_product ? (
              <StockCombobox stocks={stocks} selectedId={row.stock_id}
                             selectedLabel={row.name ? `${row.code || ""} · ${row.name}` : ""}
                             onSelect={(s) => pickStock(i, s)} index={i} />
            ) : (
              <ServiceCombobox services={services} index={i}
                               selectedLabel={row.name ? `${row.code ? row.code + " · " : ""}${row.name}` : ""}
                               onSelect={(s) => pickService(i, s)} />
            )}
            <input placeholder="Miktar" type="number" step="0.01" value={row.qty}
                   data-testid={`wo-item-${i}-qty`}
                   onChange={(e) => updateLine(i, { qty: e.target.value })} />
            <MoneyInput placeholder="Birim ₺" value={row.unit_price}
                        testid={`wo-item-${i}-price`}
                        onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
            <input placeholder="Açıklama / Seri no" value={row.description}
                   data-testid={`wo-item-${i}-desc`}
                   onChange={(e) => updateLine(i, { description: e.target.value })} />
            <button type="button" className="icon-button" onClick={() => removeLine(i)} aria-label="Satırı sil">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="wo-add-row">
        <button type="button" className="secondary-button" onClick={() => addLine(false)} data-testid="wo-add-service">
          <Plus size={14} /> Hizmet satırı
        </button>
        <button type="button" className="secondary-button" onClick={() => addLine(true)} data-testid="wo-add-product">
          <Plus size={14} /> Ürün satırı
        </button>
      </div>

      <label>Açıklama / Notlar
        <textarea data-testid="wo-note" rows="3" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </label>

      <div className="wo-footer">
        <div className="wo-total" data-testid="wo-total">Tahmini Tutar: <strong>{money(total)}</strong></div>
        {error && <div className="form-error">{error}</div>}
        {savedMsg && <div className="form-success" data-testid="wo-saved">{savedMsg}</div>}
        {onCancel && <button type="button" className="secondary-button" onClick={onCancel} data-testid="wo-edit-cancel">Vazgeç</button>}
        <button type="submit" className="primary-button" disabled={saving} data-testid="wo-submit">
          <Save size={15} /> {saving ? "Kaydediliyor..." : editId ? "Değişiklikleri kaydet" : "Muhasebeye gönder"}
        </button>
      </div>
    </form>
  );
}

export default function WorkPanel({ user, onLogout }) {
  const [mine, setMine] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["new", "list", "ruhsat", "appointments"].includes(t) ? t : "new";
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState(window.history.state, "", url);
  }, [tab]);
  const load = async () => {
    try {
      const { data } = await api.get("/work-orders");
      setMine(data);
    } catch { /* ignore */ }
    try {
      const r = await api.get("/customers");
      setCustomers(r.data);
    } catch { /* ignore */ }
    try {
      const s = await api.get("/stocks");
      setStocks(s.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); window.scrollTo(0, 0); }, []); // eslint-disable-line

  return (
    <div className="work-panel">
      <header className="work-header">
        <div className="work-brand">
          <img src="/logo-serhan.png" alt="Serhan Elektronik" className="brand-logo" />
          <span className="brand-caption">İŞ TAKİP</span>
        </div>
        <div className="work-tabs" data-testid="work-panel-tabs">
          <button data-testid="work-tab-new" className={tab === "new" ? "active" : ""} onClick={() => setTab("new")}>Yeni İş Fişi</button>
          <button data-testid="work-tab-list" className={tab === "list" ? "active" : ""} onClick={() => setTab("list")}>İş Fişleri</button>
          <button data-testid="work-tab-ruhsat" className={tab === "ruhsat" ? "active" : ""} onClick={() => setTab("ruhsat")}>Ruhsat Arşivi</button>
          <button data-testid="work-tab-appointments" className={tab === "appointments" ? "active" : ""} onClick={() => setTab("appointments")}>Randevular</button>
          <button data-testid="work-tab-installs" className={tab === "installs" ? "active" : ""} onClick={() => setTab("installs")}>UTTS ve Takip</button>
        </div>
        <div className="work-user">
          <span><Briefcase size={14} /> {user.name}</span>
          <button className="icon-button" onClick={onLogout} aria-label="Çıkış" data-testid="work-logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>
      <main className="work-main">
        {tab === "new" && (
          <section className="work-card" data-testid="work-section-new">
            <h2>Yeni İş Fişi</h2>
            <p className="muted">İş bilgilerini doldurup kaydedin, muhasebe bu fişi faturalandırma süreci için alacaktır.</p>
            <WorkOrderForm onSaved={load} stocks={stocks} />
          </section>
        )}
        {tab === "list" && (
          <section className="work-card" data-testid="work-section-list">
            <div className="wo-list-head">
              <h2>İş fişleri</h2>
              <label className="perm-inline" data-testid="wo-only-mine">
                <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Sadece benim fişlerim
              </label>
            </div>
            <p className="muted">Tüm personelin fişleri görünür. Düzenleme: kendi fişinizi aynı gün, diğerlerini yalnızca İş Takip yetkilisi yapabilir.</p>
            {mine.length === 0 && <p className="muted">Henüz fiş yok.</p>}
            <div className="wo-list">
              {mine.filter((w) => !onlyMine || w.created_by === user.id).slice(0, 50).map((w) => (
                <div key={w.id} className="wo-list-row clickable-row" data-testid={`wo-mine-${w.id}`}
                     {...rowLink(`/is-fisi/${w.id}`)}>
                  <div>
                    <strong>{w.customer_name}</strong>
                    <small>{[w.plate, w.job, w.created_by_name || "—"].filter(Boolean).join(" · ")}</small>
                  </div>
                  <div className="wo-list-right">
                    {w.cancel_request && <span className="status-pill overdue" data-testid={`wo-cancel-pill-${w.id}`} title={w.cancel_request.reason}>İptal talebi</span>}
                    {canEditWorkOrder(user, w) && <Pencil size={13} data-testid={`wo-editable-${w.id}`} />}
                    <span className={`status-pill ${w.status === "işlendi" ? "paid" : "pending"}`}>
                      {w.status === "işlendi" ? <><Check size={11} /> İşlendi</> : "Beklemede"}
                    </span>
                    <small>{humanDate(w.created_at)}</small>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {tab === "ruhsat" && (
          <section className="work-card" data-testid="work-section-ruhsat">
            <h2>Ruhsat Arşivi</h2>
            <p className="muted">Daha önce saklanan ruhsat fotoğraflarını plaka, şase no veya müşteri adıyla arayın; ya da yeni fotoğraf çekip aratın.</p>
            <RuhsatArchiveTable perPage={8} />
          </section>
        )}
        {tab === "installs" && (
          <section className="work-card" data-testid="work-section-installs">
            <InstallsTabs user={user} customers={customers} />
          </section>
        )}
        {tab === "appointments" && (
          <section className="work-card" data-testid="work-panel-appointments">
            <Appointments user={user} customers={customers}
                          onCustomerAdded={(c) => setCustomers([c, ...customers])} />
          </section>
        )}
      </main>
    </div>
  );
}
